import { useState, useEffect } from "react";
import { X, Save, AlertCircle } from "lucide-react";

const EditDesignationModal = ({
  isOpen,
  onRequestClose,
  onSave,
  designation,
  isLoading = false,
}) => {
  const [name, setName] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (designation) {
      setName(designation.name || "");
    }
    setError("");
  }, [designation]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      setError("Designation name is required");
      return;
    }

    if (name.trim().length < 2) {
      setError("Designation name must be at least 2 characters");
      return;
    }

    try {
      await onSave({ ...designation, name: name.trim() });
      onRequestClose();
    } catch (err) {
      setError(err.message || "Failed to update designation");
    }
  };

  const handleClose = () => {
    setName("");
    setError("");
    onRequestClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={handleClose}
      />

      <div
        className="fixed inset-0 flex items-center justify-center z-50"
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 transform transition-all">
          <div className="relative px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 text-center">
              Edit Designation
            </h2>
            <button
              onClick={handleClose}
              className="absolute right-2 w-10 h-10 flex  justify-center items-center top-2 text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-full hover:bg-gray-100"
              disabled={isLoading}
              type="button"
            >
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            <div className="mb-6">
              <label
                htmlFor="designation-name"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Designation Name *
              </label>
              <input
                id="designation-name"
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (error) setError("");
                }}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F22D8] focus:border-transparent transition-all ${
                  error ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Enter designation name"
                disabled={isLoading}
                autoFocus
                maxLength={50}
              />

              <div className="flex justify-between mt-1">
                <div>
                  {error && (
                    <div className="flex items-center gap-1 text-sm text-red-600">
                      <AlertCircle size={14} />
                      {error}
                    </div>
                  )}
                </div>
                <span className="text-xs text-gray-500">{name.length}/50</span>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2.5 text-sm font-medium text-white bg-[#4F22D8] hover:bg-[#3D1BA6] rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed min-w-[120px] justify-center"
                disabled={isLoading || !name.trim() || name.trim().length < 2}
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EditDesignationModal;
